import React, { useEffect, useState, useCallback, useMemo } from "react";
import { caApi } from "../../../backend_urls/caApi";
import LoadingSpinner from "../../../components/loaders/LoadingSpinner";
import PageHeader from "../../../components/reusable/management/PageHeader";
import StatsGrid from "../../../components/reusable/management/StatsGrid";
import { officeStats } from "./models/offices_model/OfficeStatsConfig";
import ManagementToolbar from "../../../components/reusable/management/ManagementToolbar";
import DynamicTable from "../../../components/reusable/DynamicTable";
import { officeColumns } from "./models/offices_model/OfficeTableData";
import CreateOffice from "./models/offices_model/CreateOffice";
import ManagementDrawer from "../../../components/reusable/management/ManagementDrawer";

const CAOfficesDashboard = () => {
    const [offices, setOffices] = useState([]);

    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState("");

    // FILTERS
    const [filters, setFilters] = useState({
        search: "",
        status: "",
        city: "",
    });

    // CREATE / EDIT FORM
    const [formOpen, setFormOpen] = useState(false);
    const [formLoading, setFormLoading] = useState(false);
    const [formError, setFormError] = useState("");

    const [formOptions, setFormOptions] = useState({
        countries: [],
        states: [],
        cities: [],
        timezones: [],
    });

    const [selectedOffice, setSelectedOffice] = useState(null);
    const [drawerOpen, setDrawerOpen] = useState(false);

    const loadOffices = useCallback(
        async (initial = false) => {
            try {
                if (initial) { setLoading(true);
                } else { setRefreshing(true); }

                setError("");

                const response = await caApi.getOffices();
                const data = response?.data ?? response;

                const officeList =
                    Array.isArray(data)
                        ? data
                        : Array.isArray(data?.results)
                            ? data.results
                            : [];

                setOffices(officeList);

            } catch (err) {
                console.error("Failed to load offices:", err);
                setError(err?.response?.data?.detail || "Unable to load offices.");
                setOffices([]);
            } finally {
                setLoading(false);
                setRefreshing(false);
            }
        },
        []
    );

    // LOAD OFFICE CREATE OPTIONS
    const loadOfficeOptions = useCallback(
        async () => {
            try {
                const response = await caApi.getOfficeCreateOptions();
                const data = response?.data ?? response;

                setFormOptions({
                    countries: data?.countries || [],
                    states: data?.states || [],
                    cities: data?.cities || [],
                    timezones: data?.timezones || [],
                });

            } catch (err) {
                console.error("Failed to load office options:", err);

                setFormOptions({
                    countries: [],
                    states: [],
                    cities: [],
                    timezones: [],
                });

                setFormError(err?.response?.data?.detail || "Unable to load office options.");
            }
        },
        []
    );

    useEffect(() => { loadOffices(true); }, [loadOffices]);

    // CITY OPTIONS
    const cityOptions = useMemo(() => {
        const cities = offices.map((office) => office.city).filter(Boolean);
        const uniqueCities = [ ...new Set(cities) ];

        return uniqueCities
            .sort((a, b) => String(a).localeCompare(String(b)))
            .map((city) => ({ value: city, label: city }));

    }, [offices]);

    // FILTER OFFICES
    const filteredOffices = useMemo(() => {
        const search = filters.search?.trim().toLowerCase() || "";
        const status = filters.status || "";
        const city = filters.city || "";

        return offices.filter((office) => {
            // SEARCH
            if (search) {
                const searchableText = [
                    office.name,
                    office.code,
                    office.city,
                    office.state,
                    office.country,
                    office.phone,
                    office.email,
                ]
                    .filter(Boolean)
                    .join(" ")
                    .toLowerCase();

                if ( !searchableText.includes(search) ) { return false; }
            }

            // STATUS
            if (status) {
                const isActive = Boolean(office.is_active);
                if (status === "active" && !isActive) { return false; }
                if (status === "inactive" && isActive) { return false; }
            }

            // CITY
            if (city && office.city !== city) { return false; }

            return true;
        });
    }, [offices, filters]);

    // RESET FILTERS
    const resetFilters = useCallback(() => {
        setFilters({ search: "", status: "", city: "" });
    }, []);

    // OPEN CREATE OFFICE FORM
    const handleCreateOffice =
        async () => {
            setFormError("");
            setSelectedOffice(null);
            await loadOfficeOptions();
            setFormOpen(true);
        };

        // CLOSE FORM
    const handleCloseForm = () => {
        if (formLoading) { return; }
        setFormOpen(false);
        setFormError("");
        setSelectedOffice(null);
    };

    // CREATE OFFICE
    const handleSubmitOffice =
        async (payload) => {
            try {
                setFormLoading(true);
                setFormError("");
                await caApi.createOffice(payload);

                // Close popup
                setFormOpen(false);

                // Clear selected office
                setSelectedOffice(null);

                // Refresh data
                await loadOffices();
            } catch (err) {
                console.error("Failed to create office:", err);

                const responseData = err?.response?.data;

                let message =
                    responseData?.detail ||
                    responseData?.message ||
                    "Unable to create office.";

                // DRF FIELD VALIDATION ERRORS
                if (
                    typeof responseData ===
                        "object" &&
                    responseData !== null &&
                    !responseData?.detail &&
                    !responseData?.message
                ) {

                    const fieldErrors =
                        Object.entries(responseData).map(
                                ([ field, errors ]) => {

                                    const errorText =
                                        Array.isArray(errors)
                                            ? errors.join(", ")
                                            : String(errors);

                                    return `${field}: ${errorText}`;
                                }
                            )
                            .join(" | ");
                    if (fieldErrors) { message = fieldErrors; }
                }

                setFormError(message);
            } finally { setFormLoading(false); }
        };

    // TABLE ACTIONS
    const actions = [
        {
            buttonType: "view",
            label: "View",
            title: "View Office",
            onClick: (office) => {
                setSelectedOffice(office);
                setDrawerOpen(true);
            },
        },
    ];

    return ( loading || refreshing ? <LoadingSpinner /> :
        <>
            {/* PAGE HEADER */}
            <PageHeader
                title="Office Management"
                subtitle="Manage all offices and branches."
                actions={[
                    {
                        label: "Create Office",
                        icon: "+",
                        onClick: handleCreateOffice,
                    },
                ]}
            />

            {/* STATS */}
            <StatsGrid cards={officeStats} />

            {/* TOOLBAR */}
            <ManagementToolbar
                values={filters}
                searchPlaceholder="Search offices..."
                filters={[
                    {
                        key: "status",
                        label: "Status",
                        placeholder: "All Statuses",
                        options: [
                            { value: "active", label: "Active" },
                            { value: "inactive", label: "Inactive" },
                        ],
                    },
                    {
                        key: "city",
                        label: "City",
                        placeholder: "All Cities",
                        options: cityOptions,
                    },
                ]}
                onChange={setFilters}
                onReset={resetFilters}
                resultCount={filteredOffices.length}
                resultLabel="offices"
                showCount
            />

            {/* ERROR */}
            {error && (
                <div
                    style={{
                        marginBottom: "12px",
                        padding: "10px 14px",
                        borderRadius: "6px",
                        background: "#fef2f2",
                        color: "#b91c1c",
                        border: "1px solid #fecaca",
                    }}
                >
                    {error}
                </div>
            )}

            {/* TABLE */}
            <DynamicTable
                columns  = { officeColumns }
                data     = { filteredOffices }
                onAction = { actions }
            />

            {/* CREATE OFFICE */}
            <CreateOffice
                isOpen={ formOpen }
                loading={ formLoading }
                error={ formError }
                options={ formOptions }
                initialValues={{}}
                isEdit={false}
                onClose={ handleCloseForm }
                onSubmit={ handleSubmitOffice }
            />

            {/* OFFICE DETAILS DRAWER */}
            {selectedOffice && (
                <ManagementDrawer
                    isOpen={ drawerOpen }
                    onClose={() => {
                        setDrawerOpen(false);
                        setSelectedOffice(null);
                    }}
                    title="Office Details"
                    sections={[
                        {
                            title: "General Information",
                            fields: [
                                {
                                    label: "Office",
                                    value: selectedOffice.name || "-",
                                },
                                {
                                    label: "Branch Code",
                                    value: selectedOffice.code || "-",
                                },
                                {
                                    label: "City",
                                    value: selectedOffice.city || "-",
                                },
                                {
                                    label: "State",
                                    value: selectedOffice.state || "-",
                                },
                                {
                                    label: "Country",
                                    value: selectedOffice.country || "-",
                                },
                                {
                                    label: "Phone",
                                    value: selectedOffice.phone || "-",
                                },
                                {
                                    label: "Email",
                                    value: selectedOffice.email || "-",
                                },
                                {
                                    label: "Status",
                                    value:
                                        selectedOffice.is_active
                                            ? "Active"
                                            : "Inactive",
                                },
                            ],
                        },

                        {
                            title: "Address",
                            fields: [
                                {
                                    label: "Address",
                                    value: selectedOffice.address || "-",
                                },
                                {
                                    label: "City",
                                    value: selectedOffice.city || "-",
                                },
                                {
                                    label: "State",
                                    value: selectedOffice.state || "-",
                                },
                                {
                                    label: "Pincode",
                                    value: selectedOffice.pincode || "-",
                                },
                            ],
                        },
                    ]}
                />
            )}
        </>
    );
};

export default CAOfficesDashboard;
